import { useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import { ROUTES } from "@/app/routes";
import type { TenantUserMapping } from "../../../../../packages/shared-types/src/index.js";
import { resolveIdentityMappingDisplayName } from "./identity-view-model.js";

export function IdentityMappingsSection({
  tenantId,
  rules,
  isLoading,
}: {
  tenantId: string;
  rules: TenantUserMapping[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="gap-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-2">
            <CardTitle>Agentiq Identity Mappings</CardTitle>
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              These connect a channel to an Agentiq user.
            </p>
          </div>
            <Button size="sm" onClick={() => navigate(ROUTES.tenantIdentityNew(tenantId))}>
            <Plus size={14} />
            Add mapping
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <TableSkeleton rows={4} cols={5} />
        ) : rules.length === 0 ? (
          <EmptyState
            title="No identity mappings"
            description="Add an Agentiq identity mapping to connect transport identities to local user IDs."
            action={{
              label: "Add mapping",
              onClick: () => navigate(ROUTES.tenantIdentityNew(tenantId)),
            }}
          />
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: "var(--bg-accent)" }}>
                  <HeaderCell>Name</HeaderCell>
                  <HeaderCell>Email</HeaderCell>
                  <HeaderCell>Phone</HeaderCell>
                  <HeaderCell>User ID</HeaderCell>
                  <HeaderCell>Channel</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr
                    key={`${rule.tenantId}:${rule.channel}:${rule.userId}`}
                    className="border-t border-border cursor-pointer hover:bg-(--bg-hover) transition-colors"
                    onClick={() =>
                      navigate(
                        ROUTES.tenantIdentityMapping(
                          tenantId,
                          rule.channel,
                          rule.userId,
                        ),
                      )
                    }
                  >
                    <BodyCell strong>{resolveIdentityMappingDisplayName(rule)}</BodyCell>
                    <BodyCell>{rule.email ?? "-"}</BodyCell>
                    <BodyCell>{rule.phone ?? "-"}</BodyCell>
                    <BodyCell mono>{rule.userId}</BodyCell>
                    <BodyCell>
                      <Pill label={rule.channel} />
                    </BodyCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="text-left px-4 py-2 font-medium"
      style={{ color: "var(--muted-foreground)" }}
    >
      {children}
    </th>
  );
}

function BodyCell({
  children,
  strong,
  mono,
}: {
  children: React.ReactNode;
  strong?: boolean;
  mono?: boolean;
}) {
  return (
    <td
      className={`px-4 py-2.5 ${strong ? "font-medium" : ""} ${mono ? "font-mono" : ""}`}
      style={{ color: strong ? "var(--text-strong)" : "var(--muted-foreground)" }}
    >
      {children}
    </td>
  );
}

function Pill({ label }: { label: string }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
      style={{
        background: "var(--bg-muted)",
        color: "var(--muted-foreground)",
      }}
    >
      {label}
    </span>
  );
}
